import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Offcanvas } from 'react-bootstrap';
import { useSnackbar } from 'notistack';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { Helmet } from 'react-helmet-async';
import Barcode from 'react-barcode';
import { useReactToPrint } from 'react-to-print';

import Preloader from '../../components/Preloader/Preloader';
import { ProductContext } from '../../ProductProvider';
import OffcanvasTemplate from '../search/OffcanvasTemplate';
import AutoComplete from '../../components/AutoComplete';
import { books_category } from '../../ProData';
import BookCover from '../../components/BookCover/BookCover';
import BarcodePrint from './BarcodePrint';
import './BarcodeMainView.css';
import './barcode.css';

const config = require('../../config.json');

export default function BarcodeMainView() {
  const { validateLogin, loggedUser } = useContext(ProductContext);
  const { enqueueSnackbar } = useSnackbar();

  // Search & Data State
  const [searchResult, setSearchResult] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [searchTopic, setSearchTopic] = useState('AccessionNo');
  const [loaded, setLoaded] = useState(true);
  const [categoryList, setCategoryList] = useState([]);

  // Selection for Batch Barcode Printing
  const [selectedAccessions, setSelectedAccessions] = useState(new Set());

  // In-table quick filter & sorting
  const [tableFilter, setTableFilter] = useState('');
  const [sortField, setSortField] = useState('bookClassificationNo');
  const [sortOrder, setSortOrder] = useState('asc');

  // Drawer / Offcanvas detail view
  const [showDrawer, setShowDrawer] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);

  // Single Item Print State
  const [singlePrintItem, setSinglePrintItem] = useState(null);

  // Print References for useReactToPrint
  const componentRef = useRef(null);
  const currentPrintRef = useRef(null);

  // Scroll to top listener
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    if (!loggedUser.isAuthenticated) {
      validateLogin();
    } else {
      const activeCats = books_category.filter((item) => item.categoryDisplay === 'Yes');
      setCategoryList(activeCats);
    }
  }, [loggedUser]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 250);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // React to Print Handlers (50mm x 25mm Label Roll Full Coverage)
  const barcodePageStyle = `
    @page {
      size: 50mm 25mm;
      margin: 0;
    }
    @media print {
      *, *:before, *:after {
        box-sizing: border-box !important;
      }
      html, body {
        margin: 0 !important;
        padding: 0 !important;
        background: #ffffff !important;
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        width: 50mm !important;
        height: 25mm !important;
      }
      .barcode-print-root {
        width: 50mm !important;
        margin: 0 !important;
        padding: 0 !important;
        display: block !important;
      }
      .barcode-container {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        width: 50mm !important;
        height: 25mm !important;
        max-width: 50mm !important;
        max-height: 25mm !important;
        box-sizing: border-box !important;
        padding: 1mm !important;
        margin: 0 auto !important;
        page-break-after: always !important;
        break-after: page !important;
        page-break-inside: avoid !important;
        break-inside: avoid !important;
      }
      .barcode-display {
        width: 100% !important;
        height: 100% !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        box-sizing: border-box !important;
        padding: 0 !important;
        margin: 0 !important;
      }
      .barcode-label {
        width: 48mm !important;
        height: 23mm !important;
        max-width: 48mm !important;
        max-height: 23mm !important;
        display: flex !important;
        flex-direction: column !important;
        justify-content: center !important;
        align-items: center !important;
        border: 0.5mm solid #000000 !important;
        box-sizing: border-box !important;
        padding: 0.5mm 1.5mm !important;
        margin: 0 auto !important;
        gap: 1px !important;
        page-break-after: always !important;
        break-after: page !important;
      }
      .barcode-call-number {
        width: 100% !important;
        font-size: 13px !important;
        font-weight: 800 !important;
        color: #000000 !important;
        line-height: 1.1 !important;
        text-align: center !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .barcode-svg-wrapper {
        width: 100% !important;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      .barcode-svg-wrapper svg {
        max-width: 100% !important;
        height: auto !important;
        display: block !important;
        margin: 0 auto !important;
      }
    }
  `;

  const handlePrintBatch = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Vidyajyoti-Barcodes-${new Date().toISOString().slice(0, 10)}`,
    pageStyle: barcodePageStyle
  });

  const handlePrintSingle = useReactToPrint({
    contentRef: currentPrintRef,
    documentTitle: `Barcode-${singlePrintItem?.accessionNo || 'Label'}`,
    pageStyle: barcodePageStyle
  });

  // Search Books in Catalogue
  const handleSearch = async (e, forcedTopic = null, forcedValue = null) => {
    if (e) e.preventDefault();
    const topicToUse = forcedTopic || searchTopic;
    const valueToUse = forcedValue !== null ? forcedValue : searchValue;

    if (!valueToUse.trim()) {
      enqueueSnackbar('Please enter a search keyword or select a category', { variant: 'warning' });
      return;
    }

    try {
      setLoaded(false);
      const { idToken } = (await fetchAuthSession()).tokens ?? {};
      const clientID = 'VJCollege';

      let keysearchValue = valueToUse.trim();
      if (topicToUse !== 'BookCategory' && topicToUse !== 'Call No') {
        keysearchValue = keysearchValue.toUpperCase();
      }

      const response = await axios.get(`${config.api.invokeURL}/vjlibrary/search/books`, {
        params: {
          fetchKey: keysearchValue,
          fetchOption: topicToUse === 'Call No' ? 'Call No' : topicToUse,
          clientID: clientID
        },
        headers: {
          Authorization: idToken,
          'x-api-key': config.api.key
        }
      });

      const rawData = Array.isArray(response.data) ? response.data : [];
      setSearchResult(rawData);

      // Select all retrieved results by default for printing convenience
      const allAccs = new Set(rawData.map((b) => b.accessionNo));
      setSelectedAccessions(allAccs);

      enqueueSnackbar(`${rawData.length} Books Found`, { variant: 'success' });
    } catch (error) {
      console.error('Barcode search error:', error);
      enqueueSnackbar('Error searching books: ' + (error.response?.data?.message || error.message), { variant: 'warning' });
    } finally {
      setLoaded(true);
    }
  };

  const handleQuickPreset = (topic, query) => {
    setSearchTopic(topic);
    setSearchValue(query);
    handleSearch(null, topic, query);
  };

  // Batch Selection Helpers
  const toggleSelectAccession = (accNo) => {
    const next = new Set(selectedAccessions);
    if (next.has(accNo)) {
      next.delete(accNo);
    } else {
      next.add(accNo);
    }
    setSelectedAccessions(next);
  };

  const handleSelectAll = () => {
    if (selectedAccessions.size === displayItems.length) {
      setSelectedAccessions(new Set());
    } else {
      const all = new Set(displayItems.map((b) => b.accessionNo));
      setSelectedAccessions(all);
    }
  };

  const handleSelectTop = (count) => {
    const topItems = displayItems.slice(0, count).map((b) => b.accessionNo);
    setSelectedAccessions(new Set(topItems));
    enqueueSnackbar(`Selected first ${topItems.length} items for barcode printing`, { variant: 'info' });
  };

  // Sorting Handler
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  // Filtered & Sorted Display Items
  const displayItems = useMemo(() => {
    let list = [...searchResult];

    // In-table keyword filter
    if (tableFilter.trim()) {
      const q = tableFilter.toLowerCase();
      list = list.filter(
        (b) =>
          (b.accessionNo || '').toLowerCase().includes(q) ||
          (b.bookTitle || b.title || '').toLowerCase().includes(q) ||
          (b.bookAuthor || b.author || '').toLowerCase().includes(q) ||
          (b.bookClassificationNo || '').toLowerCase().includes(q) ||
          (b.bookCategory || '').toLowerCase().includes(q)
      );
    }

    // Natural Alphanumeric Collator (sorts "105 S 10", "105 S 11", "105 S 100" properly)
    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

    // Sort
    list.sort((a, b) => {
      let valA = a[sortField] || '';
      let valB = b[sortField] || '';

      if (sortField === 'accessionNo') {
        const numA = parseInt(valA, 10);
        const numB = parseInt(valB, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortOrder === 'asc' ? numA - numB : numB - numA;
        }
      }

      if (sortField === 'bookClassificationNo') {
        const callA = a.bookClassificationNo || a.callNo || '';
        const callB = b.bookClassificationNo || b.callNo || '';
        if (!callA && callB) return sortOrder === 'asc' ? 1 : -1;
        if (callA && !callB) return sortOrder === 'asc' ? -1 : 1;

        const cmp = naturalCollator.compare(String(callA), String(callB));
        if (cmp !== 0) return sortOrder === 'asc' ? cmp : -cmp;

        const numA = parseInt(a.accessionNo, 10);
        const numB = parseInt(b.accessionNo, 10);
        if (!isNaN(numA) && !isNaN(numB)) {
          return sortOrder === 'asc' ? numA - numB : numB - numA;
        }
        return sortOrder === 'asc'
          ? naturalCollator.compare(String(a.accessionNo || ''), String(b.accessionNo || ''))
          : -naturalCollator.compare(String(a.accessionNo || ''), String(b.accessionNo || ''));
      }

      const cmp = naturalCollator.compare(String(valA), String(valB));
      return sortOrder === 'asc' ? cmp : -cmp;
    });

    return list;
  }, [searchResult, tableFilter, sortField, sortOrder]);

  // Items currently selected for printing (sorted in Call No ascending order)
  const itemsToPrint = useMemo(() => {
    const naturalCollator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    const selected = searchResult.filter((b) => selectedAccessions.has(b.accessionNo));

    return selected.sort((a, b) => {
      const callA = a.bookClassificationNo || a.callNo || '';
      const callB = b.bookClassificationNo || b.callNo || '';

      if (!callA && callB) return 1;
      if (callA && !callB) return -1;

      const cmp = naturalCollator.compare(String(callA), String(callB));
      if (cmp !== 0) return cmp;

      // Secondary sort: Accession No ascending
      const numA = parseInt(a.accessionNo, 10);
      const numB = parseInt(b.accessionNo, 10);
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB;
      }
      return naturalCollator.compare(String(a.accessionNo || ''), String(b.accessionNo || ''));
    });
  }, [searchResult, selectedAccessions]);

  // Single Item Print Action
  const triggerSinglePrint = (item) => {
    setSinglePrintItem(item);
    setTimeout(() => {
      handlePrintSingle();
    }, 150);
  };

  // Open Details Drawer
  const handleOpenDetail = (item) => {
    setSelectedBook(item);
    setShowDrawer(true);
  };

  // KPIs
  const kpiStats = useMemo(() => {
    const totalLoaded = searchResult.length;
    const selectedCount = selectedAccessions.size;
    const distinctCategories = new Set(searchResult.map((b) => b.bookCategory || 'General')).size;
    const availableOnShelf = searchResult.filter(
      (b) =>
        (b.bookStatus || '').toLowerCase() === 'available' ||
        (b.bookStatus || '').toLowerCase() === 'shelf'
    ).length;

    return { totalLoaded, selectedCount, distinctCategories, availableOnShelf };
  }, [searchResult, selectedAccessions]);

  return (
    <div className="barcode-page-container">
      <Helmet>
        <title>Barcode Center & Spine Label Generator | Vidyajyoti Library</title>
        <meta
          name="description"
          content="Generate and print standardized Code128 barcodes and spine labels for physical books, serials, and library catalogue items."
        />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>

      {!loaded && <Preloader show={true} />}

      <div className="barcode-wrapper">
        {/* Navigation Breadcrumbs */}
        <div className="barcode-nav-header">
          <div className="barcode-breadcrumb">
            <Link to="/">
              <i className="fa fa-home"></i> Home
            </Link>
            <span>/</span>
            <Link to="/managebooks">Catalogue Management</Link>
            <span>/</span>
            <span style={{ color: '#0f172a', fontWeight: 600 }}>Barcode & Spine Label Center</span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <Link to="/managebooks" className="btn btn-sm btn-outline-secondary" style={{ fontWeight: 600 }}>
              <i className="fa fa-book me-1"></i> Add / Manage Books
            </Link>
            <Link to="/issuereturnbooks" className="btn btn-sm btn-outline-primary" style={{ fontWeight: 600 }}>
              <i className="fa fa-exchange-alt me-1"></i> Circulation Desk
            </Link>
          </div>
        </div>

        {/* Hero Header Card */}
        <div className="barcode-hero-card">
          <div className="barcode-hero-left">
            <div className="barcode-hero-badge">
              <i className="fa fa-barcode"></i>
              <span>Librarian Technical Services</span>
            </div>
            <h1 className="barcode-hero-title">Barcode & Spine Label Printing Center</h1>
            <p className="barcode-hero-sub">
              Generate standardized Code128 barcodes with Dewey classification call numbers for book spine pasting, pocket labels, and automated circulation scanning.
            </p>
          </div>

          {/*<div className="barcode-hero-actions">
            <button
              type="button"
              className="btn-hero-print"
              onClick={handlePrintBatch}
              disabled={itemsToPrint.length === 0}
              title="Print all selected barcode labels"
            >
              <i className="fa fa-print"></i>
              <span>Print Selected ({itemsToPrint.length})</span>
            </button>
          </div>*/}
        </div>

        {/* KPI Metrics Summary Strip */}
        <div className="barcode-kpi-grid">
          <div className="barcode-kpi-card">
            <div className="barcode-kpi-header">
              <span className="barcode-kpi-label">
                <i className="fa fa-layer-group text-primary"></i> Total Loaded
              </span>
              <div className="barcode-kpi-icon" style={{ background: '#e0f2fe', color: '#0284c7' }}>
                <i className="fa fa-book"></i>
              </div>
            </div>
            <div className="barcode-kpi-val">{kpiStats.totalLoaded}</div>
            <div className="barcode-kpi-sub">Matching search query</div>
          </div>

          <div className="barcode-kpi-card">
            <div className="barcode-kpi-header">
              <span className="barcode-kpi-label">
                <i className="fa fa-check-square text-success"></i> Selected for Print
              </span>
              <div className="barcode-kpi-icon" style={{ background: '#dcfce7', color: '#15803d' }}>
                <i className="fa fa-barcode"></i>
              </div>
            </div>
            <div className="barcode-kpi-val" style={{ color: '#059669' }}>
              {kpiStats.selectedCount}{' '}
              <span style={{ fontSize: '0.85rem', color: '#64748b' }}>/ {kpiStats.totalLoaded}</span>
            </div>
            <div className="barcode-kpi-sub">Ready to output on printer</div>
          </div>

          <div className="barcode-kpi-card">
            <div className="barcode-kpi-header">
              <span className="barcode-kpi-label">
                <i className="fa fa-tags text-purple"></i> Categories
              </span>
              <div className="barcode-kpi-icon" style={{ background: '#f3e8ff', color: '#7e22ce' }}>
                <i className="fa fa-th-large"></i>
              </div>
            </div>
            <div className="barcode-kpi-val">{kpiStats.distinctCategories}</div>
            <div className="barcode-kpi-sub">Distinct classifications</div>
          </div>

          <div className="barcode-kpi-card">
            <div className="barcode-kpi-header">
              <span className="barcode-kpi-label">
                <i className="fa fa-archive text-info"></i> Shelf Availability
              </span>
              <div className="barcode-kpi-icon" style={{ background: '#ecfdf5', color: '#047857' }}>
                <i className="fa fa-box"></i>
              </div>
            </div>
            <div className="barcode-kpi-val">{kpiStats.availableOnShelf}</div>
            <div className="barcode-kpi-sub">Available for immediate label affixing</div>
          </div>
        </div>

        {/* Search & Criteria Panel */}
        <div className="barcode-search-panel">
          <form onSubmit={(e) => handleSearch(e)}>
            <div className="barcode-search-row">
              <select
                className="barcode-topic-select"
                value={searchTopic}
                onChange={(e) => setSearchTopic(e.target.value)}
              >
                <option value="AccessionNo">Accession Number / ID</option>
                <option value="Call No">Dewey Call No / Classification</option>
                <option value="BookTitle">Book Title Keyword</option>
                <option value="BookAuthor">Author Name</option>
                <option value="BookCategory">Subject Category</option>
              </select>

              {searchTopic === 'BookCategory' ? (
                <div className="barcode-input-wrapper">
                  <AutoComplete
                    data={categoryList}
                    onSelect={(val) => {
                      if (val && val.name) {
                        setSearchValue(val.name);
                        handleSearch(null, 'BookCategory', val.name);
                      }
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
              ) : (
                <div className="barcode-input-wrapper">
                  <i className="fa fa-search barcode-input-icon"></i>
                  <input
                    type="text"
                    className="barcode-search-input"
                    placeholder={`Search by ${searchTopic === 'AccessionNo' ? 'Accession ID (e.g. 1001 or B120)' : searchTopic === 'Call No' ? 'Call No (e.g. 230 or 220.5)' : 'keyword...'}`}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </div>
              )}

              <button type="submit" className="btn-barcode-search">
                <i className="fa fa-search"></i>
                <span>Search Books</span>
              </button>

              <button
                type="button"
                className="btn-barcode-reset"
                onClick={() => {
                  setSearchValue('');
                  setSearchResult([]);
                  setSelectedAccessions(new Set());
                  setTableFilter('');
                }}
              >
                <i className="fa fa-undo me-1"></i> Reset
              </button>
            </div>
          </form>

          {/* Quick Filter Presets */}
          {/*} <div className="barcode-preset-chips">
            <span style={{ fontWeight: 600, color: '#063547' }}>
              <i className="fa fa-bolt me-1 text-warning"></i> Quick Queries:
            </span>
            <span className="barcode-preset-chip" onClick={() => handleQuickPreset('BookCategory', 'Biblical Studies')}>
              Biblical Studies
            </span>
            <span className="barcode-preset-chip" onClick={() => handleQuickPreset('BookCategory', 'Theology')}>
              Theology
            </span>
            <span className="barcode-preset-chip" onClick={() => handleQuickPreset('BookCategory', 'Church History')}>
              Church History
            </span>
            <span className="barcode-preset-chip" onClick={() => handleQuickPreset('BookCategory', 'Philosophy')}>
              Philosophy
            </span>
            <span className="barcode-preset-chip" onClick={() => handleQuickPreset('Call No', '230')}>
              Call No: 230 (Dogmatics)
            </span>
            <span className="barcode-preset-chip" onClick={() => handleQuickPreset('Call No', '220')}>
              Call No: 220 (Bible)
            </span>
          </div>*/}
        </div>

        {/* Selection Toolbar & Table Filter */}
        {searchResult.length > 0 && (
          <div className="barcode-selection-toolbar">
            <div className="barcode-selection-left">
              <button type="button" className="barcode-select-all-btn" onClick={handleSelectAll}>
                <i className={`fa ${selectedAccessions.size === displayItems.length ? 'fa-check-square' : 'fa-square'} me-1`}></i>
                {selectedAccessions.size === displayItems.length ? 'Deselect All' : 'Select All'}
              </button>

              <button type="button" className="barcode-select-all-btn" onClick={() => handleSelectTop(10)}>
                Select Top 10
              </button>

              <button type="button" className="barcode-select-all-btn" onClick={() => handleSelectTop(24)}>
                Select 24 Labels
              </button>

              <span style={{ color: '#64748b', fontSize: '0.85rem' }}>
                <strong>{selectedAccessions.size}</strong> of {displayItems.length} items checked
              </span>
            </div>

            <div className="barcode-hero-actions">
              <button
                type="button"
                className="btn-hero-print"
                onClick={handlePrintBatch}
                disabled={itemsToPrint.length === 0}
                title="Print all selected barcode labels"
              >
                <i className="fa fa-print"></i>
                <span>Print Selected ({itemsToPrint.length})</span>
              </button>
            </div>

            <div style={{ position: 'relative' }}>
              <i className="fa fa-filter" style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8', fontSize: '0.8rem' }}></i>
              <input
                type="text"
                className="barcode-in-table-filter"
                placeholder="Filter results table..."
                value={tableFilter}
                onChange={(e) => setTableFilter(e.target.value)}
              />
            </div>
          </div>
        )}

        {/* Barcode Results Table */}
        {searchResult.length > 0 ? (
          <div className="barcode-table-card">
            <div style={{ overflowX: 'auto' }}>
              <table className="barcode-data-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px', textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={selectedAccessions.size === displayItems.length && displayItems.length > 0}
                        onChange={handleSelectAll}
                        title="Select All"
                      />
                    </th>
                    <th style={{ width: '45px' }}>#</th>
                    <th className="sortable" onClick={() => handleSort('accessionNo')}>
                      Accession No
                      <i className={`fa ms-1 ${sortField === 'accessionNo' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th className="sortable" onClick={() => handleSort('bookClassificationNo')}>
                      Call No
                      <i className={`fa ms-1 ${sortField === 'bookClassificationNo' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th className="sortable" onClick={() => handleSort('bookTitle')}>
                      Book Title & Author
                      <i className={`fa ms-1 ${sortField === 'bookTitle' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th className="sortable" onClick={() => handleSort('bookCategory')}>
                      Category
                      <i className={`fa ms-1 ${sortField === 'bookCategory' ? (sortOrder === 'asc' ? 'fa-sort-up' : 'fa-sort-down') : 'fa-sort'}`}></i>
                    </th>
                    <th style={{ textAlign: 'center', width: '220px' }}>Barcode Label Preview</th>
                    <th style={{ textAlign: 'center', width: '120px' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayItems.map((item, index) => {
                    const isChecked = selectedAccessions.has(item.accessionNo);

                    return (
                      <tr key={item.accessionNo || index} className={isChecked ? 'selected-row' : ''}>
                        {/* Checkbox */}
                        <td style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleSelectAccession(item.accessionNo)}
                          />
                        </td>

                        {/* Row Index */}
                        <td style={{ color: '#94a3b8', fontWeight: 600 }}>{index + 1}</td>

                        {/* Accession Number */}
                        <td>
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontWeight: 800,
                              color: '#0284c7',
                              background: '#f1f5f9',
                              padding: '3px 8px',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              navigator.clipboard.writeText(item.accessionNo);
                              enqueueSnackbar(`Copied Accession No: ${item.accessionNo}`, { variant: 'info' });
                            }}
                            title="Click to copy"
                          >
                            {item.accessionNo}
                          </span>
                        </td>

                        {/* Call Number */}
                        <td>
                          <span style={{ fontWeight: 700, color: '#063547', fontFamily: 'monospace' }}>
                            {item.bookClassificationNo || 'N/A'}
                          </span>
                        </td>

                        {/* Title & Author */}
                        <td>
                          <div style={{ fontWeight: 700, color: '#063547', lineHeight: 1.3, marginBottom: '2px' }}>
                            {item.bookTitle || item.title || 'Untitled Publication'}
                          </div>
                          <div style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            <i className="fa fa-user-edit me-1"></i>
                            {item.bookAuthor || item.author || 'Author not listed'}
                          </div>
                        </td>

                        {/* Category */}
                        <td>
                          <span className="badge bg-light text-dark border">
                            {item.bookCategory || 'General'}
                          </span>
                        </td>

                        {/* Live Barcode Label Preview */}
                        <td style={{ textAlign: 'center' }}>
                          <div className="barcode-preview-cell">
                            {item.bookClassificationNo && (
                              <div className="barcode-preview-callno">{item.bookClassificationNo}</div>
                            )}
                            <Barcode
                              value={String(item.accessionNo || '')}
                              width={1.6}
                              height={34}
                              fontSize={12}
                              margin={2}
                              displayValue={true}
                            />
                          </div>
                        </td>

                        {/* Actions */}
                        <td style={{ textAlign: 'center' }}>
                          <div className="d-flex align-items-center justify-content-center gap-1">
                            <button
                              type="button"
                              className="btn-print-row"
                              onClick={() => triggerSinglePrint(item)}
                              title="Print Single Barcode Label"
                            >
                              <i className="fa fa-print"></i>
                              <span>Print</span>
                            </button>

                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              style={{ padding: '4px 8px', borderRadius: '6px' }}
                              onClick={() => handleOpenDetail(item)}
                              title="View Full Book Details"
                            >
                              <i className="fa fa-eye"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div style={{ background: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', padding: '50px 20px', textAlign: 'center' }}>
            <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#e0f2fe', color: '#0284c7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px auto', fontSize: '1.8rem' }}>
              <i className="fa fa-barcode"></i>
            </div>
            <h5 style={{ fontWeight: 800, color: '#063547', marginBottom: '6px' }}>
              No Books Loaded in Barcode Queue
            </h5>
            <p style={{ color: '#64748b', maxWidth: '480px', margin: '0 auto 18px auto', fontSize: '0.92rem' }}>
              Select a search topic above (e.g. Accession ID, Dewey Call No, Category, or Title) to fetch catalogue items and generate printable barcode labels.
            </p>
          </div>
        )}
      </div>

      {/* Off-screen Mounted Container for Batch Printing */}
      <div className="barcode-print-mount">
        <BarcodePrint
          searchResult={itemsToPrint.length > 0 ? itemsToPrint : searchResult}
          componentRef={componentRef}
        />
      </div>

      {/* Off-screen Mounted Container for Single Item Printing */}
      <div className="barcode-print-mount">
        <BarcodePrint
          searchResult={singlePrintItem ? [singlePrintItem] : []}
          componentRef={currentPrintRef}
        />
      </div>

      {/* Offcanvas Detail Drawer */}
      <Offcanvas
        show={showDrawer}
        onHide={() => setShowDrawer(false)}
        placement="end"
        style={{ width: '460px', maxWidth: '95vw' }}
      >
        <Offcanvas.Header closeButton style={{ background: '#063547', color: '#ffffff' }}>
          <Offcanvas.Title>
            <i className="fa fa-id-card me-2"></i> Book {selectedBook?.accessionNo}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body style={{ padding: '24px', background: '#f8fafc' }}>
          {selectedBook && (
            <div>
              <div style={{ display: 'flex', gap: '16px', alignItems: 'center', background: '#ffffff', padding: '16px', borderRadius: '14px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                <BookCover
                  title={selectedBook.bookTitle || selectedBook.title}
                  author={selectedBook.bookAuthor || selectedBook.author}
                  accessionNo={selectedBook.accessionNo}
                  callNumber={selectedBook.bookClassificationNo}
                  category={selectedBook.bookCategory}
                  size="sm"
                />
                <div>
                  <span className="badge bg-primary-subtle text-primary mb-2">
                    {selectedBook.bookCategory || 'General'}
                  </span>
                  <h6 style={{ fontWeight: 800, color: '#063547', marginBottom: '4px' }}>
                    {selectedBook.bookTitle || selectedBook.title}
                  </h6>
                  <p style={{ fontSize: '0.85rem', color: '#64748b', marginBottom: 0 }}>
                    {selectedBook.bookAuthor || selectedBook.author}
                  </p>
                </div>
              </div>

              {/* Barcode Strip */}
              <div style={{ background: '#ffffff', padding: '16px', borderRadius: '12px', border: '1px solid #e2e8f0', textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#063547', marginBottom: '6px' }}>
                  {selectedBook.bookClassificationNo}
                </div>
                <Barcode
                  value={String(selectedBook.accessionNo || '')}
                  width={2.0}
                  height={46}
                  fontSize={14}
                  margin={2}
                />
                <div className="mt-3">
                  <button
                    type="button"
                    className="btn btn-primary btn-sm w-100"
                    style={{ background: '#063547', borderColor: '#063547', fontWeight: 700 }}
                    onClick={() => triggerSinglePrint(selectedBook)}
                  >
                    <i className="fa fa-print me-1"></i> Print Spine Label
                  </button>
                </div>
              </div>

              {/* Full Details Component */}
              <OffcanvasTemplate selectedBook={selectedBook} />
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>

      {/* Floating Scroll To Top Button */}
      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="btn-scroll-top"
          aria-label="Scroll to top"
          title="Back to Top"
          style={{
            position: 'fixed',
            bottom: '32px',
            right: '32px',
            zIndex: 9999,
            background: 'linear-gradient(135deg, #063547 60%, #0bb6d7 100%)',
            color: '#ffffff',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            boxShadow: '0 4px 18px rgba(6, 53, 71, 0.35)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '1.2rem',
            transition: 'all 0.25s ease'
          }}
        >
          <i className="fa fa-arrow-up"></i>
        </button>
      )}
    </div>
  );
}
